// Update state machine (Implementation Plan v02 §8.3, IR-33).
//
// The plan's states are `idle -> checking -> downloading(v) -> verified(v) ->
// waiting(v) -> active(v)`. This is a pure reducer so it can be unit-tested
// without a browser; the controller in `registerServiceWorker.ts` feeds it
// events derived from the real ServiceWorker lifecycle.
//
// Hard rule (Add9 F7): a `waiting` version is applied ONLY on an explicit
// `apply` (S26 "apply now") or at the next cold open. The reducer never moves
// `waiting -> active` on its own; that needs an `activated` event.

export type UpdatePhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'downloading'; readonly version: string }
  | { readonly kind: 'verified'; readonly version: string }
  | { readonly kind: 'waiting'; readonly version: string }
  | { readonly kind: 'active'; readonly version: string }

export type UpdateEvent =
  | { readonly type: 'check-start' }
  | { readonly type: 'check-none' }
  | { readonly type: 'download-start'; readonly version: string }
  | { readonly type: 'verify-ok'; readonly version: string }
  | { readonly type: 'verify-fail'; readonly version: string }
  | { readonly type: 'waiting'; readonly version: string }
  | { readonly type: 'apply'; readonly version: string }
  | { readonly type: 'activated'; readonly version: string }
  | { readonly type: 'error' }

export const initialUpdatePhase: UpdatePhase = { kind: 'idle' }

// Any failure (bad signature, lower version, download error, offline) keeps the
// current version running by returning to `idle` (Plan §8.3).
function stayOnCurrent(): UpdatePhase {
  return { kind: 'idle' }
}

export function updateReducer(state: UpdatePhase, event: UpdateEvent): UpdatePhase {
  switch (event.type) {
    case 'check-start':
      // Only begin a check from a settled state; ignore while already busy.
      return state.kind === 'idle' || state.kind === 'active' ? { kind: 'checking' } : state

    case 'check-none':
      return state.kind === 'checking' ? { kind: 'idle' } : state

    case 'download-start':
      return { kind: 'downloading', version: event.version }

    case 'verify-ok':
      return state.kind === 'downloading'
        ? { kind: 'verified', version: event.version }
        : state

    case 'verify-fail':
      return stayOnCurrent()

    case 'waiting':
      // Reached once the new worker is installed and (in I-1a) verified.
      return { kind: 'waiting', version: event.version }

    case 'apply':
      // Explicit user action (S26 "apply now"): the phase does not change until
      // the worker reports it actually activated. Never fabricate `active`.
      return state

    case 'activated':
      return { kind: 'active', version: event.version }

    case 'error':
      return stayOnCurrent()

    default: {
      const _exhaustive: never = event
      return _exhaustive
    }
  }
}

// True when a new version is installed and waiting; the shell may show a quiet,
// non-blocking "reopen to update" line (never a pop-up, Add9 F7).
export function isUpdateWaiting(state: UpdatePhase): boolean {
  return state.kind === 'waiting'
}
